import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../../components/common';
import './TopupHistory.css';

export default function TopupHistory() {
  const navigate = useNavigate();

  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [lastSynced, setLastSynced] = useState(null);

  const API_BASE =
    process.env.REACT_APP_API_BASE_URL || 'https://viz-sm-be.onrender.com';
  const PAGE_SIZE = 10;

  const getToken = () =>
    localStorage.getItem('authToken') ||
    localStorage.getItem('token') ||
    localStorage.getItem('jwt_token');

  const formatDateTime = (createdAt) => {
    if (!createdAt) return { date: '-', time: '-' };
    const d = new Date(createdAt);
    if (Number.isNaN(d.getTime())) return { date: '-', time: '-' };

    return {
      date: d.toLocaleDateString('en-IN', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      }),
      time: d.toLocaleTimeString('en-IN', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      }),
    };
  };

const getStatusClass = (status) => {
  if (!status) return 'pending';
  const statusStr = String(status).toUpperCase().trim();
  switch (statusStr) {
    case 'SUCCESS':
      return 'success';
    case 'FAILED':
      return 'failed';
    case 'PENDING':
      return 'pending';
    default:
      return 'pending';
  }
};


  const fetchWalletHistory = async (page = 1) => {
    try {
      page === 1 ? setLoading(true) : setLoadingMore(true);
      setError(null);

      const token = getToken();
      if (!token) throw new Error('No auth token');

      const res = await axios.get(
        `${API_BASE}/api/v1/user/wallethistory`,
        {
          params: { page, pageSize: PAGE_SIZE },
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      const txns = res.data.txns || [];

      page === 1
        ? setTransactions(txns)
        : setTransactions((p) => [...p, ...txns]);

      setHasMore(res.data.hasMore || false);
      setCurrentPage(page);
      setLastSynced(new Date());
    } catch (err) {
      setError(
        err.response?.data?.message ||
          err.response?.data?.error ||
          'Failed to load wallet history'
      );
      if (page === 1) setTransactions([]);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    fetchWalletHistory(1);
  }, []);

  const handleLoadMore = () => {
    if (!loadingMore && hasMore) {
      fetchWalletHistory(currentPage + 1);
    }
  };

  const handleBack = () => navigate('/home');
  const toggleExpand = (id) =>
    setExpandedId(expandedId === id ? null : id);

  /* ================= LOADING STATE ================= */
  if (loading && !transactions.length) {
    return (
      <div className="topup-history-root">
        <div className="topup-history-header">
          <div className="topup-history-header-side">
            <button
              className="topup-history-back-btn"
              onClick={handleBack}
            >
              ←
            </button>
          </div>
          <div className="topup-history-header-center">
            <h1 className="topup-history-title">
              Wallet Topup History
            </h1>
          </div>
          <div className="topup-history-header-side" />
        </div>

        <div className="topup-history-container">
          <div className="topup-history-status-message">
            Loading transactions...
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="topup-history-root">
      {/* ================= HEADER ================= */}
      <div className="topup-history-header">
        <div className="topup-history-header-side">
          <button
            className="topup-history-back-btn"
            onClick={handleBack}
          >
            <i className="fas fa-arrow-left"></i>
          </button>
        </div>
        <div className="topup-history-header-center">
          <h1 className="topup-history-title">
            Wallet Topup History
          </h1>
        </div>
        <div className="topup-history-header-side" />
      </div>

      {/* ================= CONTENT ================= */}
      <div className="topup-history-container">
        {error && (
          <div className="topup-history-error-message">
            {error}
          </div>
        )}

        {lastSynced && (
          <div className="topup-history-sync-row">
            <span className="topup-history-sync-label">
              Last updated :   
                {lastSynced.toLocaleTimeString('en-IN', {
                hour: '2-digit',
                minute: '2-digit',
                hour12: true,
              })}
            </span>
          </div>
        )}

        {/* ================= LIST ================= */}
        {transactions.length === 0 ? (
          <div className="topup-history-empty-card">
            <div className="empty-icon">📭</div>
            <div className="empty-title">
              No Transactions
            </div>
            <div className="empty-text">
              Your wallet topups will appear here
            </div>
          </div>
        ) : (
          <div className="topup-history-list">
            {transactions.map((txn, idx) => {
              const { date, time } = formatDateTime(txn.date);
              const expanded = expandedId === txn.id;

              return (
                <div
                  key={txn.id || idx}
                  className="topup-history-card"
                  onClick={() => toggleExpand(txn.id)}
                >
                  <div className="topup-history-card-row top">
                    <div className="topup-history-main-id">
                      <span className="value">
                        {date} • {time}
                      </span>
                    </div>

                    <span
                      className={`topup-history-status-badge ${getStatusClass(
                        txn.status
                      )}`}
                    >
                      {txn.status || 'PENDING'}
                    </span>
                  </div>

                  <div className="topup-history-card-row bottom">
                  <div className="topup-history-detail">
                    <span className={`value amount ${Number(txn.amount || 0) < 0 ? 'negative' : 'positive'}`}>
                      ₹{Number(txn.amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </div>


                  </div>

                  {expanded && txn.summary && (
                    <div className="topup-history-detail">
                      <span className="label">Type</span>
                      <span className="value">{txn.type || txn.summary || 'Unknown'}</span>
                                            <span className="label">Transaction ID</span>
                      <span className="value">
                        {txn.txnId || '-'}
                      </span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* ================= LOAD MORE ================= */}
        {hasMore && (
          <Button
            className="topup-history-primary-btn"
            onClick={handleLoadMore}
            disabled={loadingMore}
          >
            {loadingMore ? 'Loading...' : 'Load More'}
          </Button>
        )}
      </div>
    </div>
  );
}
